import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DestinationSelector = ({ selectedDestinations = [], onDestinationSelect, error }) => {
  const [selectedContinent, setSelectedContinent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const destinations = {
    'europe': {
      name: 'Europe',
      icon: 'MapPin',
      countries: [
        'United Kingdom', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Austria',
        'Belgium', 'Portugal', 'Greece', 'Norway', 'Sweden', 'Denmark', 'Finland', 'Iceland',
        'Ireland', 'Poland', 'Czech Republic', 'Hungary', 'Croatia', 'Slovenia', 'Slovakia',
        'Romania', 'Bulgaria', 'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Cyprus'
      ]
    },
    'asia': {
      name: 'Asia',
      icon: 'Mountain',
      countries: [
        'Japan', 'China', 'India', 'Thailand', 'Singapore', 'Malaysia', 'Indonesia', 'Philippines',
        'Vietnam', 'Cambodia', 'Laos', 'Myanmar', 'South Korea', 'Taiwan', 'Hong Kong', 'Macau',
        'Nepal', 'Bhutan', 'Sri Lanka', 'Maldives', 'Bangladesh', 'Pakistan', 'Afghanistan',
        'Iran', 'Iraq', 'Jordan', 'Lebanon', 'Syria', 'Turkey', 'Israel', 'Palestine', 'Saudi Arabia',
        'UAE', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Yemen', 'Kazakhstan', 'Uzbekistan',
        'Kyrgyzstan', 'Tajikistan', 'Turkmenistan', 'Mongolia', 'North Korea'
      ]
    },
    'north-america': {
      name: 'North America',
      icon: 'Flag',
      countries: [
        'United States', 'Canada', 'Mexico', 'Guatemala', 'Belize', 'El Salvador', 'Honduras',
        'Nicaragua', 'Costa Rica', 'Panama', 'Cuba', 'Jamaica', 'Haiti', 'Dominican Republic',
        'Puerto Rico', 'Trinidad and Tobago', 'Barbados', 'Bahamas', 'Antigua and Barbuda',
        'Saint Lucia', 'Grenada', 'Saint Vincent and the Grenadines', 'Dominica', 'Saint Kitts and Nevis'
      ]
    },
    'south-america': {
      name: 'South America',
      icon: 'TreePine',
      countries: [
        'Brazil', 'Argentina', 'Chile', 'Peru', 'Colombia', 'Venezuela', 'Ecuador', 'Bolivia',
        'Paraguay', 'Uruguay', 'Guyana', 'Suriname', 'French Guiana'
      ]
    },
    'africa': {
      name: 'Africa',
      icon: 'Sun',
      countries: [
        'South Africa', 'Kenya', 'Tanzania', 'Morocco', 'Egypt', 'Tunisia', 'Algeria', 'Libya',
        'Sudan', 'Ethiopia', 'Somalia', 'Djibouti', 'Eritrea', 'Uganda', 'Rwanda', 'Burundi',
        'Democratic Republic of Congo', 'Republic of Congo', 'Central African Republic', 'Chad',
        'Cameroon', 'Nigeria', 'Niger', 'Mali', 'Burkina Faso', 'Ghana', 'Togo', 'Benin',
        'Ivory Coast', 'Liberia', 'Sierra Leone', 'Guinea', 'Guinea-Bissau', 'Senegal',
        'Gambia', 'Mauritania', 'Western Sahara', 'Cape Verde', 'São Tomé and Príncipe',
        'Equatorial Guinea', 'Gabon', 'Angola', 'Zambia', 'Zimbabwe', 'Botswana', 'Namibia',
        'Lesotho', 'Swaziland', 'Madagascar', 'Mauritius', 'Seychelles', 'Comoros', 'Mayotte'
      ]
    },
    'oceania': {
      name: 'Oceania',
      icon: 'Waves',
      countries: [
        'Australia', 'New Zealand', 'Fiji', 'Papua New Guinea', 'New Caledonia', 'Solomon Islands',
        'Vanuatu', 'Samoa', 'Tonga', 'Kiribati', 'Palau', 'Marshall Islands', 'Micronesia',
        'Nauru', 'Tuvalu', 'Cook Islands', 'French Polynesia', 'American Samoa', 'Guam',
        'Northern Mariana Islands'
      ]
    },
    'antarctica': {
      name: 'Antarctica',
      icon: 'Snowflake',
      countries: ['Antarctica']
    }
  };

  const getAllCountries = () => {
    return Object.values(destinations)?.flatMap(continent => 
      continent?.countries?.map(country => ({
        country,
        continent: continent?.name
      }))
    );
  };

  const getFilteredCountries = () => {
    if (!searchTerm && !selectedContinent) return [];
    
    let countries = [];
    
    if (selectedContinent) {
      countries = destinations?.[selectedContinent]?.countries || [];
    } else {
      countries = getAllCountries()?.map(item => item?.country);
    }
    
    if (searchTerm) {
      countries = countries?.filter(country =>
        country?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }
    
    return countries;
  };

  const handleContinentSelect = (continentKey) => {
    setSelectedContinent(continentKey === selectedContinent ? '' : continentKey);
    setSearchTerm('');
  };

  const handleCountrySelect = (country) => {
    // Toggle country in multi-select array
    const isSelected = selectedDestinations?.includes(country);
    
    if (isSelected) {
      // Remove country from selection
      onDestinationSelect(selectedDestinations?.filter(c => c !== country));
    } else {
      // Add country to selection
      onDestinationSelect([...selectedDestinations, country]);
    }
  };

  const handleRemoveCountry = (country) => {
    onDestinationSelect(selectedDestinations?.filter(c => c !== country));
  };

  return (
    <div className="space-y-6">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Countries you operate in</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Only receive enquiries for countries you actually work in.
          </p>
        </div>
        {selectedDestinations?.length > 0 && (
          <div className="px-3 py-1 bg-primary/10 rounded-full">
            <span className="text-sm font-medium text-primary">
              {selectedDestinations?.length} selected
            </span>
          </div>
        )}
      </div>

      {/* Selected Countries Display */}
      {selectedDestinations?.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4 bg-muted/50 rounded-lg">
          {selectedDestinations?.map((country) => (
            <div
              key={country}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm"
            >
              <Icon name="MapPin" size={14} />
              <span>{country}</span>
              <button
                onClick={() => handleRemoveCountry(country)}
                className="hover:bg-primary-foreground/20 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${country}`}
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search destinations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>
      {/* Continent Selection */}
      {!searchTerm && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Select Continent</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(destinations)?.map(([key, continent]) => (
              <button
                key={key}
                onClick={() => handleContinentSelect(key)}
                className={`p-4 rounded-lg border-2 text-center transition-all hover:scale-105 ${
                  selectedContinent === key
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className={`p-3 rounded-lg mx-auto mb-2 w-fit ${
                  selectedContinent === key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={continent?.icon} size={24} />
                </div>
                <h4 className={`font-medium ${
                  selectedContinent === key ? 'text-primary' : 'text-foreground'
                }`}>
                  {continent?.name}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {continent?.countries?.length} countries
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Country Selection */}
      {(selectedContinent || searchTerm) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {searchTerm ? 'Search Results' : `Countries in ${destinations?.[selectedContinent]?.name}`}
            </h3>
            {selectedContinent && !searchTerm && (
              <button
                onClick={() => setSelectedContinent('')}
                className="text-primary hover:text-primary/80 text-sm font-medium"
              >
                ← Back to continents
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {getFilteredCountries()?.map((country) => {
              const isSelected = selectedDestinations?.includes(country);
              
              return (
                <button
                  key={country}
                  onClick={() => handleCountrySelect(country)}
                  className={`p-3 rounded-lg border text-left transition-all hover:scale-102 ${
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground shadow-md'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className={
                        isSelected ? 'text-primary-foreground' : 'text-muted-foreground'
                      } />
                      <span className="font-medium">{country}</span>
                    </div>
                    {isSelected && (
                      <Icon name="Check" size={16} className="text-primary-foreground" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {getFilteredCountries()?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="MapPin" size={32} className="mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {searchTerm ? `No destinations found matching "${searchTerm}"` : 'No countries available'}
              </p>
            </div>
          )}
        </div>
      )}
      {error && (
        <p className="text-error text-sm">{error}</p>
      )}
    </div>
  );
};

export default DestinationSelector;